/**
 * Analytics Dashboard Component
 * Displays comprehensive analytics including risk scores, anomalies, recommendations, and graph insights
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon,
  BubbleChart as GraphIcon,
  // AccountTree as NetworkIcon,  // Knowledge Graph moved to separate screen
  PersonOff as DormantIcon,
  Shield as ShieldIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  GppBad as SuspiciousIcon,
} from '@mui/icons-material';
import RiskScoresCard from './RiskScoresCard';
import AnomaliesCard from './AnomaliesCard';
import RecommendationsCard from './RecommendationsCard';
import GraphAnalyticsCard from './GraphAnalyticsCard';
// KnowledgeGraphCard moved to separate GraphRAG screen
import DormantUsersCard from './DormantUsersCard';
import PrivilegeCreepDashboard from './PrivilegeCreepDashboard';
import ExcessiveAccessDashboard from './ExcessiveAccessDashboard';
import SuspiciousActivityDashboard from './SuspiciousActivityDashboard';
import RiskPostureDashboard from './RiskPostureDashboard';
import TrustDegradationDashboard from './TrustDegradationDashboard';
import UsersListComponent from './UsersListComponent';
import apiService from '../services/apiService';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function AnalyticsDashboard({ tenantName }) {
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [dormantCount, setDormantCount] = useState(0);
  const [privilegeCreepCount, setPrivilegeCreepCount] = useState(0);
  const [excessiveAccessCount, setExcessiveAccessCount] = useState(0);
  const [suspiciousActivityCount, setSuspiciousActivityCount] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Configure API service
  useEffect(() => {
    if (tenantName) {
      apiService.setTenant(tenantName);
    }
  }, [tenantName]);

  // Define fetch functions before useEffect that uses them
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Get auth token from Auth0
      const token = await getAccessTokenSilently();
      apiService.setAuthToken(token);

      // Fetch all analytics
      const response = await apiService.getAllAnalytics({ threshold: 0.7 });

      if (response.success) {
        setAnalyticsData(response.data.results);
        setLastUpdate(new Date(response.data.processed_at));
      } else {
        throw new Error(response.error || 'Failed to fetch analytics');
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchDormantCount = useCallback(async () => {
    try {
      // Get auth token from Auth0
      const token = await getAccessTokenSilently();
      apiService.setAuthToken(token);

      // Fetch dormant users count (just get first page to see total)
      const response = await apiService.getAIDormantInsights({ page: 1, pageSize: 1 });

      if (response.success) {
        setDormantCount(response.data?.total || 0);
      }
    } catch (err) {
      console.error('Error fetching dormant count:', err);
    }
  }, [getAccessTokenSilently]);

  const fetchPrivilegeCreepCount = useCallback(async () => {
    try {
      // Get auth token from Auth0
      const token = await getAccessTokenSilently();
      apiService.setAuthToken(token);

      // Fetch privilege creep summary
      const response = await apiService.getPrivilegeCreepSummary();

      if (response.success) {
        // apiService wraps the API response, so response.data contains the API's response
        // API returns: { success: true, data: { summary: {...} } }
        // apiService wraps: { success: true, data: <API_RESPONSE> }
        // So we need: response.data.data.summary
        const summary = response.data?.data?.summary || {};
        const criticalCount = summary.critical?.count || 0;
        const highCount = summary.high?.count || 0;
        setPrivilegeCreepCount(criticalCount + highCount);
      }
    } catch (err) {
      console.error('Error fetching privilege creep count:', err);
    }
  }, [getAccessTokenSilently]);

  const fetchExcessiveAccessCount = useCallback(async () => {
    try {
      // Get auth token from Auth0
      const token = await getAccessTokenSilently();
      apiService.setAuthToken(token);

      // Fetch excessive access summary
      const response = await apiService.getExcessiveAccessSummary();

      if (response.success) {
        // apiService wraps the backend response, so we need response.data.data
        const summary = response.data?.data || {};
        const criticalCount = summary.critical_count || 0;
        const highCount = summary.high_count || 0;
        const moderateCount = summary.moderate_count || 0;
        setExcessiveAccessCount(criticalCount + highCount + moderateCount);
      }
    } catch (err) {
      console.error('Error fetching excessive access count:', err);
    }
  }, [getAccessTokenSilently]);

  const fetchSuspiciousActivityCount = useCallback(async () => {
    try {
      // Get auth token from Auth0
      const token = await getAccessTokenSilently();
      apiService.setAuthToken(token);

      // Fetch suspicious activity summary
      const response = await apiService.getSuspiciousActivitySummary();

      if (response.success) {
        // apiService wraps the backend response
        const summary = response.data || {};
        const highRisk = summary.high_risk_count || 0;
        const mediumRisk = summary.medium_risk_count || 0;
        setSuspiciousActivityCount(highRisk + mediumRisk);
      }
    } catch (err) {
      console.error('Error fetching suspicious activity count:', err);
    }
  }, [getAccessTokenSilently]);

  // Fetch analytics on mount - GUARD: Wait for Auth0 to complete
  useEffect(() => {
    // Don't fetch if Auth0 is still loading or user is not authenticated
    if (authLoading) {
      console.log('Auth0 is loading, waiting...');
      setLoading(true); // Show loading state while Auth0 loads
      return;
    }

    if (!isAuthenticated) {
      console.log('User not authenticated');
      setLoading(false);
      return;
    }

    // Auth0 is ready and user is authenticated - fetch data
    console.log('Auth0 ready, fetching analytics...');
    fetchAnalytics();
    fetchDormantCount();
    fetchPrivilegeCreepCount();
    fetchExcessiveAccessCount();
    fetchSuspiciousActivityCount();
  }, [isAuthenticated, authLoading, fetchAnalytics, fetchDormantCount, fetchPrivilegeCreepCount, fetchExcessiveAccessCount, fetchSuspiciousActivityCount]); // Re-run when auth state changes

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleRefresh = () => {
    fetchAnalytics();
    fetchDormantCount();
    fetchPrivilegeCreepCount();
    fetchExcessiveAccessCount();
    fetchSuspiciousActivityCount();
  };

  // Show loading state while Auth0 is initializing
  if (authLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Initializing authentication...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Show message if not authenticated (shouldn't happen due to App.js guard, but just in case)
  if (!isAuthenticated) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">
          Please log in to view analytics dashboard.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Analytics Dashboard
        </Typography>
        <Box>
          {lastUpdate && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
              Last updated: {lastUpdate.toLocaleString()}
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && !analyticsData && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Analytics Content */}
      {analyticsData && (
        <>
          {/* Summary Cards - AI-Powered Insights */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <WarningIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  High Risk Users
                </Typography>
                <Typography variant="h4">
                  {analyticsData.risk_scoring?.high_risk_users?.length || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 3 }
                }}
                onClick={() => setActiveTab(1)}
              >
                <ShieldIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Privilege Creep
                </Typography>
                <Typography variant="h4">
                  {privilegeCreepCount}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 3 }
                }}
                onClick={() => setActiveTab(2)}
              >
                <TrendingUpIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Excessive Access
                </Typography>
                <Typography variant="h4">
                  {excessiveAccessCount}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 3 }
                }}
                onClick={() => setActiveTab(5)}
              >
                <SuspiciousIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Suspicious Activity
                </Typography>
                <Typography variant="h4">
                  {suspiciousActivityCount}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 3 }
                }}
                onClick={() => setActiveTab(6)}
              >
                <DormantIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Dormant Accounts
                </Typography>
                <Typography variant="h4">
                  {dormantCount}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <WarningIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Anomalies Detected
                </Typography>
                <Typography variant="h4">
                  {analyticsData.anomaly_detection?.total_anomalies || 0}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Tabbed Content - Organized by TrustX Pillars */}
          <Paper>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {/* IARPM - Identity & Access Risk Posture Management */}
              <Tab label="Risk Posture" icon={<SecurityIcon />} iconPosition="start" sx={{ fontWeight: 'bold' }} />
              <Tab label="Privilege Creep" icon={<ShieldIcon />} iconPosition="start" />
              <Tab label="Excessive Access" icon={<TrendingUpIcon />} iconPosition="start" />
              <Tab label="Risk Scores" icon={<SecurityIcon />} iconPosition="start" />
              {/* Trust Degradation - Insider Risk & Predictive Alerts */}
              <Tab label="Trust Degradation" icon={<TrendingDownIcon />} iconPosition="start" />
              <Tab label="Suspicious Activity" icon={<SuspiciousIcon />} iconPosition="start" />
              <Tab label="Dormant Users" icon={<DormantIcon />} iconPosition="start" />
              <Tab label="Anomalies" icon={<WarningIcon />} iconPosition="start" />
              {/* Insights & Actions */}
              <Tab label="Recommendations" icon={<LightbulbIcon />} iconPosition="start" />
              <Tab label="Graph Analytics" icon={<GraphIcon />} iconPosition="start" />
              <Tab label="Users List" icon={<PeopleIcon />} iconPosition="start" />
            </Tabs>

            {/* IARPM Tabs */}
            <TabPanel value={activeTab} index={0}>
              <RiskPostureDashboard onNavigateToTab={setActiveTab} />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <PrivilegeCreepDashboard />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <ExcessiveAccessDashboard />
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
              <RiskScoresCard
                riskData={analyticsData.risk_scoring}
                loading={loading}
              />
            </TabPanel>

            {/* Trust Degradation Tabs */}
            <TabPanel value={activeTab} index={4}>
              <TrustDegradationDashboard onNavigateToTab={setActiveTab} />
            </TabPanel>

            <TabPanel value={activeTab} index={5}>
              <SuspiciousActivityDashboard />
            </TabPanel>

            <TabPanel value={activeTab} index={6}>
              <DormantUsersCard
                loading={loading}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={7}>
              <AnomaliesCard
                anomaliesData={analyticsData.anomaly_detection}
                loading={loading}
              />
            </TabPanel>

            {/* Insights & Actions Tabs */}
            <TabPanel value={activeTab} index={8}>
              <RecommendationsCard
                recommendationsData={analyticsData.recommendations}
                loading={loading}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={9}>
              <GraphAnalyticsCard
                graphData={analyticsData.graph_analytics}
                loading={loading}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={10}>
              <UsersListComponent />
            </TabPanel>
          </Paper>
        </>
      )}
    </Container>
  );
}

export default AnalyticsDashboard;
